/**
 * Canonical section card — the recurring
 *   `${getCardBackground} rounded-lg p-6 border ${getLightBorder}`
 * pattern, parametrised.
 *
 *   <Card>{content}</Card>                     // padding md, border, no shadow
 *   <Card padding="sm">{compact}</Card>        // p-3
 *   <Card padding="lg" shadow="md">{hero}</Card>
 *   <Card border={false} shadow="sm">{flat}</Card>
 *   <Card padding="none">{fullBleedTable}</Card>
 *   <Card background={false} className="bg-red-50 dark:bg-red-900/20">{tinted}</Card>
 *
 * Use `padding="none"` for content that must reach the card edge — a table,
 * an image strip, a disclosure header that is itself the button — and pad the
 * inner element instead. Do NOT try to reach this by passing `p-0` in
 * `className`: `className` is composed last in the string, but Tailwind
 * resolves same-specificity utilities by stylesheet source order, so `p-3`
 * still wins.
 */

import React from "react";
import { useTheme } from "../../context/ThemeContext";
import { getCardBackground, getLightBorder } from "../../utils/themeUtils";
import {
  TOKENS,
  type CardPadding,
  type CardShadow,
} from "../../utils/designTokens";

interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Padding bucket. Defaults to `md` (p-4 sm:p-6). */
  padding?: CardPadding;
  /** Render the 1px border. Defaults to true. */
  border?: boolean;
  /** Shadow elevation. Defaults to `none`. */
  shadow?: CardShadow;
  /**
   * Paint the themed card background. Defaults to true.
   *
   * Set false when the caller needs to supply its own background — a
   * severity-tinted panel (`bg-red-50 dark:bg-red-900/20`), or a frame that is
   * only a card at some breakpoints (`sm:bg-white`).
   *
   * This has to be a prop rather than something a caller overrides through
   * `className`, for the same reason `padding="none"` does: `className` is
   * composed last in the STRING, but Tailwind resolves same-specificity
   * utilities by stylesheet SOURCE order, so `bg-white` beats a later
   * `bg-transparent` or `bg-green-50`. Both UI sweeps hit this and left frames
   * hand-rolled because of it.
   */
  background?: boolean;
  children?: React.ReactNode;
}

const Card: React.FC<CardProps> = ({
  padding = "md",
  border = true,
  shadow = "none",
  background = true,
  className = "",
  children,
  ...rest
}) => {
  const { isDarkMode } = useTheme();

  const classes = [
    background ? getCardBackground(isDarkMode) : "",
    TOKENS.card.radius,
    TOKENS.card.padding[padding],
    border ? `${TOKENS.card.border} ${getLightBorder(isDarkMode)}` : "",
    TOKENS.card.shadow[shadow],
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={classes} {...rest}>
      {children}
    </div>
  );
};

export default Card;
